import { inject, Injectable } from '@angular/core';
import { EMPTY, lastValueFrom, Observable, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { expand, map, reduce } from 'rxjs/operators';
import { LocationReading } from '../models/location-reading';
import { BackendResponse } from '@fc-core/models/response.model';

export interface LocationHistory {
  id: number;
  duration: { lower: string; upper: string; bounds: string };
  location: any;
  vehicle: number;
  type?: string;
}

export interface LocationSearchPayload {
  location?: number;
  duration__range_lower?: string;
  duration__range_upper?: string;
  vehicle?: number;
}

export interface LocationHistoryStats {
  id: number;
  totalInsideTime: number;
  ingressCount: number;
  egressCount: number;
}

export interface VehicleLocationVisitSummary {
  totalInsideTime: number;
  ingressCount: number;
  egressCount: number;
}

export interface VehicleLocationHistoryResponse {
  vehicle: VehicleLocationVisitSummary;
  locationHistory: LocationHistory[];
}

export interface LocationHistogramBucket {
  entered: number;
  exited: number;
  inside: number;
}

@Injectable({
  providedIn: 'root',
})
export class LocationHistoryService {
  private readonly http = inject(HttpClient);

  loadAllVehicleHistoryData(
    searchPayload: LocationSearchPayload,
  ): Observable<LocationHistory[]> {
    const searchParams = Object.entries(searchPayload).filter(
      ([, value]) => value !== undefined && value !== null,
    );
    if (!searchParams.length) return of([]);
    const params = searchParams.reduce(
      (accumulated, [key, value]) => accumulated.append(key, String(value)),
      new HttpParams(),
    );

    // TODO: replace hand-rolled pagination loop with fetchAllPages (@fc-shared/utils/request-utils/fetch-all-pages)
    return this.http
      .get<
        BackendResponse<LocationHistory>
      >(environment.apiUrl + 'api/location-history/', { params })
      .pipe(
        expand((results) =>
          results.next
            ? this.http.get<BackendResponse<LocationHistory>>(results.next)
            : EMPTY,
        ),
        map((response) => response.results),
        reduce(
          (accumulated, page) => accumulated.concat(page),
          [] as LocationHistory[],
        ),
      );
  }

  getVehicleLocationHistory(
    locationId: number,
    vehicleId: number,
    startDate: string,
    endDate: string,
  ): Observable<VehicleLocationHistoryResponse> {
    return this.http.get<VehicleLocationHistoryResponse>(
      `${environment.apiUrl}api/locations/${locationId}/vehicles/${vehicleId}/?duration__range_lower=${startDate}&duration__range_upper=${endDate}`,
    );
  }

  getLocationReadings(
    locationHistoryId: number,
  ): Observable<LocationReading[]> {
    return this.http.get<LocationReading[]>(
      `${environment.apiUrl}api/location-history/${locationHistoryId}/readings/`,
    );
  }

  getLocationChartStats(
    payload: LocationSearchPayload,
  ): Promise<Record<string, LocationHistogramBucket>> {
    return lastValueFrom(
      this.http.get<Record<string, LocationHistogramBucket>>(
        `${environment.apiUrl}api/locations/${payload.location}/histogram/?duration__range_upper=${payload.duration__range_upper}&duration__range_lower=${payload.duration__range_lower}`,
      ),
    );
  }

  getVehicleLocationStatistics(
    payload: LocationSearchPayload,
  ): Observable<LocationHistoryStats[]> {
    return this.http
      .get<{
        results: LocationHistoryStats[];
      }>(
        `${environment.apiUrl}api/locations/${payload.location}/vehicles/?duration__range_upper=${payload.duration__range_upper}&duration__range_lower=${payload.duration__range_lower}`,
      )
      .pipe(map((response) => response.results));
  }
}
